/**
 * Sign in, and sign up — two forms, one file, because they differ by one field and a verb.
 *
 * Against Better Auth's `/api/auth/*`, through the Vite proxy so the session cookie is
 * first-party. Nothing here talks to `/v1` or `/trpc`: a user without a session has no
 * organization, and asking for one before there is a session is what `ORG_NOT_SPECIFIED`
 * exists to refuse.
 *
 * **The password floor is 12 and it is stated before it is enforced.** `instance.ts` sets
 * `minPasswordLength: 12` (§12's SOC 2 posture — a length floor is the cheapest control
 * on the list), so the form says so at the field rather than rejecting after the fact.
 * Every error states the rule, then the likely cause, then the fix.
 */
import { useState, type FormEvent, type ReactNode } from "react";
import { useNavigate } from "@tanstack/react-router";

import { Button, Input, Surface, SurfaceBody, SurfaceFooter } from "@packagepage/ui";

import { authClient, authErrorMessage } from "../api/auth.js";

import "@packagepage/ui/tokens.css";
import "@packagepage/ui/surface.css";
import "@packagepage/ui/button.css";
import "@packagepage/ui/input.css";
import "./auth.css";

/** Better Auth's floor, mirrored here so the form can say it before it enforces it. */
const MIN_PASSWORD_LENGTH = 12;

function Wordmark(): ReactNode {
  return (
    <div className="pp-auth__wordmark">
      <span className="pp-auth__mark" aria-hidden="true">
        p
      </span>
      <span>packagepage</span>
    </div>
  );
}

export function SignIn({
  mode,
  onSignedIn,
  onSwitch,
}: {
  mode: "signin" | "signup";
  /** Where to go instead of `/`. The invitation screen stays on its own token. */
  onSignedIn?: () => void;
  /** Swap forms in place, so the invitation's address is not lost to a navigation. */
  onSwitch?: () => void;
}): ReactNode {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);

  const signingUp = mode === "signup";

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);

    if (signingUp && password.length < MIN_PASSWORD_LENGTH) {
      setError(
        `A password here is at least ${MIN_PASSWORD_LENGTH} characters. That is longer ` +
          `than most sites ask for, deliberately — this account can publish a recall ` +
          `notice. A short phrase you will remember beats a short password you will not.`,
      );
      return;
    }

    setBusy(true);
    const result = signingUp
      ? await authClient.signUp.email({ email, password, name })
      : await authClient.signIn.email({ email, password });
    setBusy(false);

    if (result.error) {
      setError(authErrorMessage(result.error));
      return;
    }
    // A null token means no session was issued: the account waits on its email link. Never
    // null while requireEmailVerification is off — U64a part 2 turns it on.
    if (result.data.token === null) {
      setSentTo(email);
      return;
    }
    // Where to next is not this screen's decision. `/` resolves the caller's memberships
    // and sends them to the bootstrap or the shell — one place that knows, rather than
    // two forms each guessing.
    if (onSignedIn !== undefined) onSignedIn();
    else await navigate({ to: "/" });
  }

  if (sentTo !== null) {
    return (
      <div className="pp-auth" data-screen-label="Check your email">
        <div className="pp-auth__panel">
          <Wordmark />
          <Surface className="pp-auth__card" sectioned>
            <SurfaceBody>
              <div className="pp-auth__title">Check your email</div>
              <p className="pp-auth__lede">
                We sent a link to {sentTo}. Open it to confirm your address, then sign in.
              </p>
            </SurfaceBody>
            <SurfaceFooter>
              <p className="pp-auth__switch">
                <a
                  href="/signin"
                  onClick={(event) => {
                    if (onSwitch === undefined) return;
                    event.preventDefault();
                    setSentTo(null);
                    onSwitch();
                  }}
                >
                  Sign in
                </a>
              </p>
            </SurfaceFooter>
          </Surface>
        </div>
      </div>
    );
  }

  return (
    <div className="pp-auth" data-screen-label={signingUp ? "Sign up" : "Sign in"}>
      <div className="pp-auth__panel">
        <Wordmark />
        {/*
          The card. A default `Surface` is `size="card"` → neutral + filled → `--pp-surface`
          on `--pp-bg`, which is the same white-card-on-grey-canvas every dashboard screen
          is built from — reached through the primitive rather than by restyling
          `.pp-auth__panel`, because "cards are one component" is the whole Surface bet.

          The wordmark stays OUTSIDE it — the product identifying itself is not part of what
          is being filled in — and is centred over it. The switch line is INSIDE, as the
          card's own footer region: `sectioned` is the primitive's answer to "a separate
          section beneath", giving a sunken strip flush to the card's edges under a rule,
          rather than a fourth component that would look like a footer and not be one.

          The error Surface nests inside this one legally — `neutral` is not in Surface's
          TONED set, so the "a toned Surface may not contain another toned Surface" throw
          does not fire, and the error stays the one coloured thing on the screen.
        */}
        <Surface className="pp-auth__card" sectioned>
          <SurfaceBody>
            <div className="pp-auth__card-stack">
              <div>
                <div className="pp-auth__title">
                  {signingUp ? "Create your account" : "Sign in"}
                </div>
                <p className="pp-auth__lede">
                  {signingUp
                    ? "Your brand name comes next. No card, and no GS1 paperwork to start."
                    : "Welcome back."}
                </p>
              </div>

              {error === null ? null : (
                <Surface tone="error" emphasis="filled">
                  {error}
                </Surface>
              )}

              <form className="pp-auth__form" onSubmit={(event) => void submit(event)}>
                {/*
                  `Input` renders its own label — the component owns the pairing so that a
                  field cannot ship without one, which is why these are not wrapped in a
                  hand-written <label>. c24 made that call and this screen inherits it.
                */}
                {signingUp ? (
                  <Input
                    label="Your name"
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    autoComplete="name"
                    required
                  />
                ) : null}

                <Input
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  autoComplete="email"
                  required
                />

                <div className="pp-auth__field">
                  <Input
                    label="Password"
                    type="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    autoComplete={signingUp ? "new-password" : "current-password"}
                    required
                  />
                  {signingUp ? (
                    <span className="pp-auth__slug">
                      At least {MIN_PASSWORD_LENGTH} characters.
                    </span>
                  ) : (
                    <span className="pp-auth__slug">
                      <a href="/forgot-password">Forgot your password?</a>
                    </span>
                  )}
                </div>

                <Button type="submit" variant="primary" loading={busy}>
                  {busy
                    ? signingUp
                      ? "Creating your account…"
                      : "Signing in…"
                    : signingUp
                      ? "Create account"
                      : "Sign in"}
                </Button>
              </form>
            </div>
          </SurfaceBody>

          <SurfaceFooter>
            <p className="pp-auth__switch">
              {signingUp ? "Already have an account? " : "New here? "}
              <a
                href={signingUp ? "/signin" : "/signup"}
                onClick={(event) => {
                  if (onSwitch === undefined) return;
                  event.preventDefault();
                  onSwitch();
                }}
              >
                {signingUp ? "Sign in" : "Create an account"}
              </a>
            </p>
          </SurfaceFooter>
        </Surface>
      </div>
    </div>
  );
}
